import { FormEvent, useContext, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import Button from '../components/UI/Button';
import ErrorMessages from '../components/UI/ErrorMessages';
import InputWithFloatingButtons from '../components/UI/InputWithFloatingButtons';
import Loader from '../components/UI/Loader';
import DataTable from '../components/UI/DataTable';
import { DataTableColumn } from '../models/DataTable';
import useElementVisible from '../hooks/useElementVisible';
import useSortableList from '../hooks/useSortableList';
import User from '../models/serviceModels/User';
import { activateUser, deactivateUser } from '../services/userService';
import { MeContext, OrganizationContext } from '../App';
import { FREE_PLAN_TYPE_ID } from '../constants/planTypes';
import WarningMessages from '../components/UI/WarnginMessages';

const COLUMNS: DataTableColumn[] = [
    { key: 'fullName', label: 'Name', sortable: true },
    { key: 'userRoleName', label: 'Role', sortable: true },
    { key: 'email', label: 'Email', sortable: true },
    { key: 'phoneNumber', label: 'Phone Number', sortable: true },
    { key: 'status', label: 'Status', sortable: true },
    { key: 'edit', label: '', sortable: false },
    { key: 'actions', label: '', sortable: false },
];

const QUERY_CONFIG = {
    take: 10,
    defaultSortBy: 'fullName',
    defaultSortDirection: 'asc' as const,
    entity: 'users'
};

const ManageUsers = () => {
    const [query, setQuery] = useState('');
    const [submittedQuery, setSubmittedQuery] = useState('');
    const [toggleActivation, setToggleActivation] = useState(false);
    const [activationError, setActivationError] = useState('');
    const [reference, visible] = useElementVisible<HTMLDivElement>();
    const me = useContext(MeContext);
    const organizationContext = useContext(OrganizationContext);

    const queryClient = useQueryClient();
    const { data: users, isLoading, isFetching, sortBy, sortDirection, onSort, resetSort, triggerSearch, fetchNextPage, hasNextPage } = useSortableList<User>({
        entity: QUERY_CONFIG.entity,
        filter: { query: submittedQuery },
        take: QUERY_CONFIG.take,
        defaultSortBy: QUERY_CONFIG.defaultSortBy
    });

    useEffect(() => {
        document.title = 'Manage Users';
    }, []);

    useEffect(() => {
        organizationContext.refresh();
    }, []);

    useEffect(() => {
        if (visible && hasNextPage && !isFetching) fetchNextPage();
    }, [visible, hasNextPage, isFetching, fetchNextPage]);

    const handleOnSubmit = (e: FormEvent) => {
        e.preventDefault();
        setSubmittedQuery(query);
        triggerSearch();
    };

    const handleReset = () => {
        setQuery('');
        setSubmittedQuery('');
        resetSort();
    };

    const maxFreeUsers = organizationContext.value?.id === 'd21795f4-4e3c-4346-986b-f3057f7f3980' ? 3 : 2;
    const lockDownUsers = organizationContext.value?.planTypeId === FREE_PLAN_TYPE_ID &&
        organizationContext.value?.activeUserCount >= maxFreeUsers;

    const handleOnActivate = (userId: string) => {
        setActivationError('');
        if (lockDownUsers) return;
        setToggleActivation(true);

        activateUser(userId).then(() => {
            organizationContext.refresh();
            queryClient.invalidateQueries({ queryKey: ['users'] });
        }).catch(e => {
            console.error(e);
            setActivationError('Failed to activate user');
        }).finally(() => setToggleActivation(false));
    };

    const handleOnDeactivate = (userId: string) => {
        setActivationError('');
        setToggleActivation(true);

        deactivateUser(userId).then(() => {
            organizationContext.refresh();
            queryClient.invalidateQueries({ queryKey: ['users'] });
        }).catch(e => {
            console.error(e);
            setActivationError('Failed to deactivate user');
        }).finally(() => setToggleActivation(false));
    };

    const getResults = () => (
        <div className="row margin-vertical-2 justify-content-center">
            <div className="column-medium">
                {organizationContext.value?.planTypeId === FREE_PLAN_TYPE_ID && (
                    <WarningMessages messages={['You can have up to 2 active users in the Free Plan. Upgrade to a paid plan if you need to create more users for your organization.']} />
                )}
                <div style={{ opacity: isFetching ? 0.5 : 1, transition: 'opacity 0.2s ease' }}>
                <DataTable
                    columns={COLUMNS}
                    data={users}
                    sortBy={sortBy}
                    sortDirection={sortDirection}
                    onSort={onSort}
                    disabled={isFetching}
                    renderRow={(u: User) => (
                        <tr key={u.id} className="table-row">
                            <td>{u.firstName} {u.lastName}</td>
                            <td>{u.userRoleName}</td>
                            <td>{u.email}</td>
                            <td>{u.phoneNumber}</td>
                            <td>{u.deactivated ? 'Inactive' : 'Active'}</td>
                            <td>
                                <Button text="Edit"
                                    className="small"
                                    id={`edit-button-${u.id}`}
                                    href={`/users/${u.id}/edit`} />
                            </td>
                            <td>
                                {me.user?.id !== u.id && (
                                    <div className="row">
                                        {u.deactivated && (
                                            <div className="column-auto">
                                                <Button text="Activate"
                                                    disabled={toggleActivation || lockDownUsers}
                                                    className="small secondary"
                                                    id={`activate-button-${u.id}`}
                                                    onClick={() => handleOnActivate(u.id)} />
                                            </div>
                                        )}
                                        {!u.deactivated && (
                                            <div className="column-auto">
                                                <Button text="Deactivate"
                                                    disabled={toggleActivation}
                                                    className="small secondary"
                                                    id={`deactivate-button-${u.id}`}
                                                    onClick={() => handleOnDeactivate(u.id)} />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </td>
                        </tr>
                    )}
                />
                </div>
            </div>
        </div>
    );

    return (
        <section id="main-content" tabIndex={-1}>
            {isLoading && <Loader />}
            {!isLoading && (
                <>
                    <h1>Manage Users</h1>
                    <div className="row justify-content-center margin-vertical-2">
                        <div className="column-medium">
                            <ErrorMessages messages={[activationError]} />
                            <div className="row">
                                <div className="column-medium">
                                    <form onSubmit={handleOnSubmit}>
                                        <div className="row">
                                            <div className="column">
                                                <label htmlFor='filter-users-input-text'>Filter Users</label>
                                                <InputWithFloatingButtons
                                                    id="filter-users-input"
                                                    placeholder="Search by name, email, phone or role"
                                                    value={query}
                                                    onChange={(e) => setQuery(e)}
                                                    buttons={[{ text: 'Reset', onClick: handleReset, className: 'secondary' }]} />
                                            </div>
                                            <div className="column-auto align-self-end">
                                                <Button id="filter-users-button"
                                                    className="secondary"
                                                    type="submit"
                                                    text="Filter"
                                                    disabled={isFetching}
                                                    loading={isFetching} />
                                            </div>
                                        </div>
                                    </form>
                                </div>
                                <div className="column-medium align-self-end">
                                    <div className="row justify-content-end align-items-center">
                                        <div className="column-medium-auto">
                                            {toggleActivation && <Loader />}
                                        </div>
                                        <div className="column-medium-auto">
                                            <div className="row align-items-center">
                                                <div className="column-auto">
                                                    <Button id="create-user-button"
                                                        text="Create User"
                                                        href="/admin/users/create"
                                                        disabled={lockDownUsers} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {me.user && users.length > 0 && getResults()}
                    {me.user && users.length === 0 && !isFetching && (
                        <div className="row justify-content-center margin-vertical-2">
                            <div className="column-auto">
                                <p>No Users Found</p>
                            </div>
                        </div>
                    )}
                    {hasNextPage && (
                        <div ref={reference} className="padding-5 margin-bottom-5"></div>
                    )}
                </>
            )}
        </section>
    );
};

export default ManageUsers;
